import type { Layout } from 'react-grid-layout';
import type { DashboardGridItem } from '@aidkitorg/component-library';
import type { AdaptedDashboardComponent } from '../adapters/distroToAdminPanel';

const GRID_COLS = 6;

/**
 * Default size presets based on component/visualization kind (w x h in grid units).
 * Default size presets based on component/visualization kind.
 */
type SizePreset = { w: number; h: number; minW: number; minH: number };

const SIZE_BY_KIND: Record<string, SizePreset> = {
  // Numbers - compact 1x2 (160px)
  'Hero Number':    { w: 1, h: 2, minW: 1, minH: 2 },
  'Hero Count':     { w: 1, h: 2, minW: 1, minH: 2 },
  'Custom Number':  { w: 1, h: 2, minW: 1, minH: 2 },
  // Charts - 2x4
  'Bar Chart':        { w: 2, h: 4, minW: 2, minH: 4 },
  'Custom Bar Chart': { w: 2, h: 4, minW: 2, minH: 4 },
  'Pie Chart':        { w: 2, h: 4, minW: 2, minH: 4 },
  'Custom Pie Chart': { w: 2, h: 4, minW: 2, minH: 4 },
  'Line Chart':       { w: 2, h: 4, minW: 2, minH: 4 },
  // Maps - 5x8 (640px)
  'Map':                { w: 5, h: 8, minW: 3, minH: 6 },
  'Map Region Density': { w: 5, h: 8, minW: 3, minH: 6 },
  // Payment Dashboard - full width table
  'Payment Dashboard': { w: 6, h: 8, minW: 3, minH: 4 },
  // Tables / Reports - full width 6x8 (640px)
  'Summary Table':    { w: 6, h: 8, minW: 2, minH: 4 },
  'Table':            { w: 6, h: 8, minW: 2, minH: 4 },
  'Applicant Table':  { w: 6, h: 8, minW: 2, minH: 4 },
  'Report':           { w: 6, h: 8, minW: 5, minH: 6 },
  // Download card
  'Download':             { w: 1, h: 2, minW: 1, minH: 2 },
  'Data Dictionary':      { w: 2, h: 2, minW: 1, minH: 2 },
  // Special types
  'Dashboard Section':      { w: 6, h: 1, minW: 2, minH: 1 },
  'Dashboard Explanation':  { w: 6, h: 2, minW: 2, minH: 1 },
  'Queue':                  { w: 6, h: 2, minW: 3, minH: 2 },
};

const DEFAULT_SIZE: SizePreset = { w: 4, h: 6, minW: 1, minH: 2 };

/**
 * Get the effective kind for sizing purposes.
 * For precomputed data, uses the visualization kind; otherwise uses the audit kind.
 */
export function getEffectiveKind(adapted: AdaptedDashboardComponent): string {
  if (adapted.precomputedData) {
    return adapted.precomputedData.visualization.kind;
  }
  if (adapted.adapted) {
    return adapted.adapted.kind;
  }
  return adapted.audit.componentKind;
}

/**
 * Full-width kinds that should start on a new row (clear: both equivalent).
 */
const FULL_WIDTH_KINDS = new Set([
  'Dashboard Section', 'Queue',
  'Summary Table', 'Table', 'Applicant Table', 'Report',
  'Payment Dashboard',
]);

// A Queue with an applicants table needs a full table's worth of height, not the
// compact card preset.
export const QUEUE_WITH_TABLE_SIZE: SizePreset = { w: 6, h: 8, minW: 3, minH: 6 };

/**
 * Get the default size for a component kind.
 */
export function getSizeForKind(kind: string): SizePreset {
  return SIZE_BY_KIND[kind] || DEFAULT_SIZE;
}

/**
 * Like getSizeForKind, but inspects the component config so a Queue rendering its
 * applicants table gets a taller preset than a counts-only Queue card.
 */
export function getSizeForComponent(adapted: AdaptedDashboardComponent): SizePreset {
  const kind = getEffectiveKind(adapted);
  if (kind === 'Queue' && (adapted.original as any)?.showApplicantsTable) {
    return QUEUE_WITH_TABLE_SIZE;
  }
  return getSizeForKind(kind);
}

/**
 * Look up the default size for a grid item using its adapted component entry.
 */
export function getDefaultSizeForItem(
  item: DashboardGridItem<any>,
  adaptedMap: Map<string, { adapted: AdaptedDashboardComponent; index: number }>,
): SizePreset {
  const entry = adaptedMap.get(item.id);
  if (entry) {
    return getSizeForComponent(entry.adapted);
  }
  // For newly staged items not yet in the adapted map, use the config kind
  const kind = item.data?.config?.kind;
  if (kind) {
    return getSizeForKind(kind);
  }
  return DEFAULT_SIZE;
}

/**
 * Generate default grid layouts for adapted dashboard components.
 * Packs items left-to-right in rows, with full-width components
 * starting on new rows.
 */
export function generateDefaultLayouts(
  components: AdaptedDashboardComponent[],
  idPrefix: string,
): Layout[] {
  const layouts: Layout[] = [];
  let cursorX = 0;
  let cursorY = 0;
  let rowMaxH = 0;

  for (let i = 0; i < components.length; i++) {
    const adapted = components[i];
    const kind = getEffectiveKind(adapted);
    const size = getSizeForComponent(adapted);
    const w = Math.min(size.w, GRID_COLS);
    const h = size.h;

    const isFullWidth = FULL_WIDTH_KINDS.has(kind) || w >= GRID_COLS;

    // If full-width or doesn't fit in current row, move to next row
    if (isFullWidth || cursorX + w > GRID_COLS) {
      if (cursorX > 0) {
        cursorY += rowMaxH;
        cursorX = 0;
        rowMaxH = 0;
      }
    }

    layouts.push({
      i: `${idPrefix}-${i}`,
      x: cursorX,
      y: cursorY,
      w,
      h,
    });

    cursorX += w;
    rowMaxH = Math.max(rowMaxH, h);

    // Full-width items force a row break after themselves
    if (isFullWidth) {
      cursorY += h;
      cursorX = 0;
      rowMaxH = 0;
    }
  }

  return layouts;
}

/**
 * Backfill `_style` onto the distro components behind `adapted` (mutating each
 * component in place) so every component the grid renders has a persisted position.
 *
 * Mirrors `buildDistroGridItems`: layouts are packed per level — top-level components
 * in the dashboard's grid, each Dashboard Section's children in that section's own
 * nested grid — so a backfilled position is exactly the default the grid was already
 * rendering. Components that already have a `_style` keep it.
 *
 * Walks the adapted tree rather than the raw distro tree because that is what layouts
 * are generated from: one layout per adapted entry, at every level. A single flat index
 * shared across levels drifts as soon as a section has children (they consume top-level
 * indices) and then runs off the end of the list, leaving trailing top-level components
 * permanently style-less.
 */
export function assignDefaultStyles(
  adapted: AdaptedDashboardComponent[],
  idPrefix: string,
): void {
  const defaultLayouts = generateDefaultLayouts(adapted, idPrefix);

  adapted.forEach((comp, idx) => {
    const original = comp.original as any;
    const layout = defaultLayouts[idx];
    // A Templated Block's iterations share one template child object, so the first
    // iteration's layout wins — the rest are repositioned at render time by
    // `repositionTemplatedSiblings`.
    if (original && layout && !original._style) {
      original._style = { x: layout.x, y: layout.y, w: layout.w, h: layout.h };
    }
    if (comp.children?.length) {
      assignDefaultStyles(comp.children, `${idPrefix}-${idx}`);
    }
  });
}

/**
 * Reposition consecutive items that share identical style values so they
 * flow left-to-right with wrapping instead of stacking on top of each other.
 * Mutates the `layout` of affected items in place.
 */
export function repositionTemplatedSiblings(
  items: Array<DashboardGridItem<any>>,
  getStyle: (index: number) => { x: number; y: number; w: number; h: number } | undefined,
): void {
  const sameStyle = (a: any, b: any) =>
    a && b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

  let i = 0;
  while (i < items.length) {
    const style = getStyle(i);
    if (!style) { i++; continue; }

    let j = i + 1;
    while (j < items.length && sameStyle(getStyle(j), style)) j++;

    if (j - i > 1) {
      let x = style.x;
      let y = style.y;
      for (let k = i; k < j; k++) {
        if (x + style.w > GRID_COLS) { x = 0; y += style.h; }
        items[k].layout = { ...items[k].layout, x, y } as any;
        x += style.w;
      }
    }

    i = j;
  }
}

/**
 * Build grid items for distro dashboard components.
 * Uses persisted `_style` from each component when available,
 * falling back to `generateDefaultLayouts` for components without styles.
 */
export function buildDistroGridItems(
  adapted: AdaptedDashboardComponent[],
  dashboardId: string,
): Array<DashboardGridItem<any>> {
  const defaultLayouts = generateDefaultLayouts(adapted, dashboardId);
  const items = adapted.map((comp, idx) => {
    const compId = `${dashboardId}-${idx}`;
    const savedStyle = (comp.original as any)?._style;
    let layout = savedStyle
      ? { i: compId, x: savedStyle.x, y: savedStyle.y, w: savedStyle.w, h: savedStyle.h }
      : defaultLayouts[idx];

    // Queues styled before they could show an applicants table have a saved height
    // sized for just the card. RGL won't grow them to minH on its own, so bump the
    // saved height up to the table preset's minimum when the table is enabled.
    if (
      layout &&
      getEffectiveKind(comp) === 'Queue' &&
      (comp.original as any)?.showApplicantsTable &&
      layout.h < QUEUE_WITH_TABLE_SIZE.minH
    ) {
      layout = { ...layout, h: QUEUE_WITH_TABLE_SIZE.minH };
    }
    const isSection = comp.audit.componentKind === 'Dashboard Section' && !!comp.children;
    return {
      id: compId,
      data: {
        id: compId,
        config: comp.adapted || { kind: comp.audit.componentKind },
      },
      layout,
      autoHeight: isSection || undefined,
    };
  });

  // Fix up templated siblings that share the same _style values.
  repositionTemplatedSiblings(
    items,
    (idx) => (adapted[idx].original as any)?._style,
  );

  return items;
}
